import Constants from "expo-constants";

import { getApiBase } from "./api-env";
import { getAccessToken, refreshAccessToken } from "./auth";
import { postForm } from "./post-form";

/**
 * The build making the call. The server reads this to decide whether this app
 * can render the read-and-sign contract; with no version it fails closed and
 * strips the contract from the Compliance screen.
 *
 * This file has its own `request()` rather than using `lib/api.ts`, and that
 * copy never sent the header — so every compliance fetch arrived version-less
 * and the contract was invisible to every nurse, on every build. Only nurses
 * who tapped the push (which deep-links straight to `/compliance/<docId>`,
 * bypassing the list) could sign. Fixed 2026-09-30.
 */
const APP_VERSION = Constants.expoConfig?.version ?? "unknown";

/** RN-facing compliance API client — mirrors the onboarding-api request flow. */
async function request<T>(path: string, options?: RequestInit): Promise<T> {
    const token = getAccessToken();
    const headers: Record<string, string> = {
        "Content-Type": "application/json",
        "X-App-Version": APP_VERSION,
        ...(options?.headers as Record<string, string>)
    };
    if (token) {
        headers["Authorization"] = `Bearer ${token}`;
    }

    let res = await fetch(`${getApiBase()}${path}`, { ...options, headers });

    if (res.status === 401 && token) {
        const newToken = await refreshAccessToken();
        if (newToken) {
            headers["Authorization"] = `Bearer ${newToken}`;
            res = await fetch(`${getApiBase()}${path}`, { ...options, headers });
        } else {
            // Refresh returned null — a transient failure (network/timeout/5xx/
            // 429). performRefresh now owns the logout-on-401 decision, so we
            // must NOT log the nurse out here. Surface a retryable error.
            throw new Error("Couldn't refresh your session. Please try again.");
        }
    }

    if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const msg =
            typeof body.error === "string"
                ? body.error
                : body.error?.message || `Request failed: ${res.status}`;
        throw new Error(msg);
    }

    return res.json();
}

// ── Types (match packages/api compliance-rn.service) ──

export type ComplianceStatusType = "issue" | "expiringSoon" | "pending" | "compliant";

export type ComplianceDocStatus =
    "compliant" | "not_compliant" | "resolved" | "not_due_yet" | "missing" | "pending_uploads";

export interface ComplianceRequirement {
    kind: "item" | "group";
    itemId: number | null;
    groupId: number | null;
    name: string;
    status: ComplianceDocStatus;
    statusType: ComplianceStatusType;
    subtitle: string;
    docId: number | null;
    effectiveDate: string | null;
    expiryDate: string | null;
    rejectionReason: string | null;
    /** Crawler-verified (RN license) — auto-renews; app hides the upload CTA. */
    autoVerified: boolean;
    /**
     * Set for a course item (the yearly scope-of-practice refresher): the card
     * opens this training bundle, there is nothing to upload. Optional because
     * older servers do not send it.
     */
    training?: { bundleSlug: string; estMinutes: number } | null;
    members: {
        itemId: number;
        name: string;
        itemType: string;
        status: ComplianceDocStatus;
        docId: number | null;
        expiryDate: string | null;
    }[];
}

export interface ComplianceBanner {
    tone: "error" | "warning";
    title: string;
    message: string;
    buttonLabel: string;
}

export interface RnComplianceView {
    rnId: number;
    banner: ComplianceBanner | null;
    groups: {
        issues: ComplianceRequirement[];
        expiring: ComplianceRequirement[];
        pending: ComplianceRequirement[];
        compliant: ComplianceRequirement[];
    };
}

export interface ComplianceRecordFieldValue {
    fieldId: number;
    choiceId: number | null;
    valueText: string | null;
}

export interface ComplianceRecord {
    id: number;
    status: ComplianceDocStatus;
    effectiveDate: string | null;
    expiryDate: string | null;
    uploadId: number | null;
    fileType: string | null;
    fileName: string | null;
    supersededAt: string | null;
    verifiedAt: string | null;
    rejectionReason: string | null;
    createdAt: string;
    fieldValues: ComplianceRecordFieldValue[];
}

export interface ComplianceFieldChoice {
    id: number;
    label: string;
    orderIndex: number;
}

/**
 * Post-doc metadata field on a compliance item (see compliance_item_fields).
 * Distinct from a form question — fields are filled by the admin/AI AFTER
 * the doc exists, and drive per-choice follow-ups.
 */
export interface ComplianceItemField {
    id: number;
    fieldKey: string;
    label: string;
    fieldType: string;
    mandatory: boolean;
    orderIndex: number;
    shownWhenFieldId: number | null;
    shownWhenChoiceId: number | null;
    /** Present on dropdown fields; empty otherwise. */
    choices: ComplianceFieldChoice[];
}

/** The five authored question types — drives the mobile widget choice. */
export type ComplianceFormQuestionType =
    "plain_text" | "free_text" | "checkbox" | "radio" | "signature";

export interface ComplianceFormQuestionChoice {
    id: number;
    label: string;
    orderIndex: number;
}

/**
 * One row of an authored compliance_form. Each question is rendered by a
 * specific widget per `questionType`. For radio/checkbox `choices` is
 * non-empty; for every other type it's an empty array.
 */
export interface ComplianceFormQuestion {
    id: number;
    questionKey: string;
    label: string;
    questionType: ComplianceFormQuestionType;
    mandatory: boolean;
    orderIndex: number;
    choices: ComplianceFormQuestionChoice[];
}

/**
 * One answer the RN posts. Shape varies by question type:
 *   - free_text  → valueText
 *   - radio      → choiceIds: [oneId]
 *   - checkbox   → choiceIds: [...]
 *   - signature  → valueText (base64 PNG data URI)
 *   - plain_text → not submitted at all
 */
export interface ComplianceFormAnswer {
    questionId: number;
    valueText?: string | null;
    choiceIds?: number[];
}

export interface RnPendingUpload {
    submissionId: number;
    status: string;
    rejectionReason: string | null;
    submittedAt: string | null;
    uploadId: number | null;
    fileType: string | null;
    fileName: string | null;
}

export interface RnComplianceDocDetail {
    itemId: number;
    itemName: string;
    /** The item's type — `compliance_form` triggers the form renderer; everything else uses the upload flow. */
    itemType: string;
    /**
     * Whether the RN may upload/submit right now. False when the credential is
     * already compliant and not yet inside its renewal window — the UI hides
     * the upload/submit CTA (the backend enforces this independently too).
     */
    canSubmit: boolean;
    /** Crawler-verified (RN license): renews automatically against the state
     * board. The app hides the upload CTA and shows reassurance copy instead. */
    autoVerified: boolean;
    /** Optional helper sentence shown under the title (any item type). */
    itemDescription: string | null;
    activeRecord: ComplianceRecord | null;
    previousRecords: ComplianceRecord[];
    pendingUploads: RnPendingUpload[];
    /** Post-doc metadata fields. */
    fields: ComplianceItemField[];
    /** Authored form questions — only populated when itemType === "compliance_form". */
    questions: ComplianceFormQuestion[];
    /**
     * The RN employment contract's agreement markdown — non-null ONLY for the
     * "Contract signature" item. When present, the detail screen renders the
     * read-and-sign contract (ContractDocument + signature) instead of the
     * upload flow, and submits via `signContract`.
     */
    contractMarkdown: string | null;
}

// ── Display helpers ──

export const DOC_STATUS_LABEL: Record<ComplianceDocStatus, string> = {
    compliant: "Compliant",
    not_compliant: "Not compliant",
    resolved: "Resolved",
    not_due_yet: "Not due yet",
    missing: "Missing",
    pending_uploads: "Pending approval"
};

/**
 * Maps a raw 6-value doc status to one of the four mobile buckets.
 *
 * Pass `expiryDate` so a still-compliant credential within the warn
 * window resolves as `"expiringSoon"` rather than `"compliant"` — the
 * old version was expiry-blind and the detail hero (icon + chrome)
 * showed a happy green "Compliant" tile for a doc about to expire.
 *
 * `today` defaults to the current date in ISO yyyy-mm-dd.
 * `warnBeforeDays` defaults to 30 — matches the legacy `expiring_30d`
 * alert tier and the admin-side `daysUntilExpiry` threshold.
 */
export function docStatusToType(
    status: ComplianceDocStatus,
    opts?: { expiryDate?: string | null; today?: string; warnBeforeDays?: number }
): ComplianceStatusType {
    if (status === "missing" || status === "not_compliant") {
        return "issue";
    }
    if (status === "pending_uploads") {
        return "pending";
    }
    if (status === "compliant" && opts?.expiryDate) {
        const today = opts.today ?? new Date().toISOString().slice(0, 10);
        const warn = opts.warnBeforeDays ?? 30;
        const days = Math.floor(
            (Date.parse(`${String(opts.expiryDate).slice(0, 10)}T00:00:00Z`) -
                Date.parse(`${today}T00:00:00Z`)) /
                86_400_000
        );
        if (days >= 0 && days <= warn) {
            return "expiringSoon";
        }
    }
    return "compliant";
}

/**
 * Hero-card label for a single doc record. Translates the 6-value
 * status PLUS the expiry context into something an RN can scan in one
 * read: "Compliant" / "Expires in 12 days" / "Expired 3 days ago" /
 * "Pending review" / etc. The detail screen used to fall back to the
 * raw status string, which read "Compliant" for an expiring doc.
 */
export function docStatusLabel(
    status: ComplianceDocStatus,
    opts?: { expiryDate?: string | null; today?: string; warnBeforeDays?: number }
): string {
    const today = opts?.today ?? new Date().toISOString().slice(0, 10);
    if (status === "missing") {
        return "Missing";
    }
    if (status === "not_compliant") {
        if (opts?.expiryDate) {
            const days = Math.floor(
                (Date.parse(`${String(opts.expiryDate).slice(0, 10)}T00:00:00Z`) -
                    Date.parse(`${today}T00:00:00Z`)) /
                    86_400_000
            );
            if (days < 0) {
                return `Expired ${-days} day${-days === 1 ? "" : "s"} ago`;
            }
        }
        return "Not compliant";
    }
    if (status === "pending_uploads") {
        return "Pending review";
    }
    if (status === "resolved") {
        return "Compliant";
    }
    if (status === "not_due_yet") {
        return "Not due yet";
    }
    // compliant — qualify with expiry context when relevant.
    if (opts?.expiryDate) {
        const days = Math.floor(
            (Date.parse(`${String(opts.expiryDate).slice(0, 10)}T00:00:00Z`) -
                Date.parse(`${today}T00:00:00Z`)) /
                86_400_000
        );
        const warn = opts.warnBeforeDays ?? 30;
        if (days >= 0 && days <= warn) {
            if (days === 0) {
                return "Expires today";
            }
            return `Expires in ${days} day${days === 1 ? "" : "s"}`;
        }
    }
    return "Compliant";
}

// ── Calls ──

export function fetchRnCompliance(rnId: string | number): Promise<RnComplianceView> {
    return request<RnComplianceView>(`/rn/${rnId}/compliance`);
}

export function fetchRnComplianceDoc(
    rnId: string | number,
    docId: string | number
): Promise<RnComplianceDocDetail> {
    return request<RnComplianceDocDetail>(`/rn/${rnId}/compliance/docs/${docId}`);
}

export function acknowledgeComplianceExpiry(
    rnId: string | number,
    docId: string | number
): Promise<{ acknowledged: number }> {
    return request<{ acknowledged: number }>(
        `/rn/${rnId}/compliance/docs/${docId}/acknowledge-expiry`,
        { method: "POST", body: "{}" }
    );
}

export function renewComplianceUploadUrl(
    rnId: string | number,
    itemId: string | number,
    body: { filename: string; contentType: string }
): Promise<{ uploadId: number; uploadUrl: string }> {
    return request<{ uploadId: number; uploadUrl: string }>(
        `/rn/${rnId}/compliance/items/${itemId}/renew/upload-url`,
        { method: "POST", body: JSON.stringify(body) }
    );
}

export function renewComplianceConfirm(
    rnId: string | number,
    uploadId: string | number
): Promise<{ confirmed: boolean; uploadId: number }> {
    return request<{ confirmed: boolean; uploadId: number }>(
        `/rn/${rnId}/compliance/uploads/${uploadId}/renew/confirm`,
        { method: "POST", body: "{}" }
    );
}

/**
 * Multi-file renewal — sends N files in one multipart POST. The backend
 * stitches them into a single combined PDF (or returns the lone file
 * as-is for the single-file fast path) before running AI verification.
 *
 * Each entry's `uri` is a `file://` or `blob:` URL from the picker, sent as a
 * React Native `{ uri, name, type }` FormData part. Posted with `postForm`
 * (XMLHttpRequest), not `fetch`: since Expo SDK 56 the global `fetch` refuses
 * those parts. Bypasses the regular `request()` helper because the multipart
 * body must NOT get the `Content-Type: application/json` default header:
 * the request sets its own multipart boundary header, which is the only way
 * the server can parse the parts.
 */
export async function renewComplianceCombined(
    rnId: string | number,
    itemId: string | number,
    files: Array<{ uri: string; contentType: string; filename: string }>
): Promise<{ confirmed: boolean; uploadId: number }> {
    if (files.length === 0) {
        throw new Error("renewComplianceCombined: at least one file is required");
    }
    const form = new FormData();
    for (const f of files) {
        // React Native's FormData accepts this `{ uri, name, type }`
        // shape natively — it streams the file from disk into the
        // multipart body without ever loading it into JS memory.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        form.append("files", { uri: f.uri, name: f.filename, type: f.contentType } as any);
    }

    const token = getAccessToken();
    const headers: Record<string, string> = {};
    if (token) {
        headers["Authorization"] = `Bearer ${token}`;
    }

    const url = `${getApiBase()}/rn/${rnId}/compliance/items/${itemId}/renew/combined`;
    let res = await postForm(url, headers, form);
    if (res.status === 401 && token) {
        const newToken = await refreshAccessToken();
        if (newToken) {
            headers["Authorization"] = `Bearer ${newToken}`;
            // FormData is consumed by the first fetch — rebuild it for the retry.
            const retry = new FormData();
            for (const f of files) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                retry.append("files", { uri: f.uri, name: f.filename, type: f.contentType } as any);
            }
            res = await postForm(url, headers, retry);
        } else {
            // Refresh returned null — a transient failure (network/timeout/5xx/
            // 429). performRefresh now owns the logout-on-401 decision, so we
            // must NOT log the nurse out here. Surface a retryable error.
            throw new Error("Couldn't refresh your session. Please try again.");
        }
    }
    if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const msg =
            typeof body.error === "string"
                ? body.error
                : body.error?.message || `Request failed: ${res.status}`;
        throw new Error(msg);
    }
    return res.json();
}

/**
 * Submit answers for a compliance_form item (e.g. flu declination, fit-test
 * attestation). The server creates/updates one rn_submission per
 * (onboarding, item) and flips it to "submitted" for admin review.
 */
export function submitComplianceFormAnswers(
    rnId: string | number,
    itemId: string | number,
    answers: ComplianceFormAnswer[]
): Promise<{ submissionId: number; status: string }> {
    return request<{ submissionId: number; status: string }>(
        `/rn/${rnId}/compliance/items/${itemId}/submit-form`,
        { method: "POST", body: JSON.stringify({ answers }) }
    );
}

/**
 * Re-sign the RN employment contract from the Compliance screen. `signature` is
 * the drawn signature as a `data:image/...` URL. Regenerates the real executed
 * contract PDF and creates a fresh, non-expiring compliance record.
 */
export function signContract(
    rnId: string | number,
    signature: string
): Promise<{ complianceDocId: number | null }> {
    return request<{ complianceDocId: number | null }>(`/rn/${rnId}/compliance/contract/sign`, {
        method: "POST",
        body: JSON.stringify({ signature })
    });
}
